import React, { useState, useEffect, useRef } from "react";

export interface ImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src?: string;
  alt: string;
  fallback?: string;
  aspectRatio?: "square" | "video" | "auto";
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
  /**
   * Enable lazy loading using Intersection Observer API
   * When true, image will only load when it enters the viewport
   * Default: false (uses native loading attribute if provided)
   */
  useLazyLoading?: boolean;
  /**
   * Root margin for Intersection Observer (e.g., "50px" or "10%")
   * Images will start loading this distance before entering viewport
   * Default: "50px"
   */
  rootMargin?: string;
  /**
   * Placeholder image URL to show while loading
   * If not provided, uses fallback or a solid color placeholder
   */
  placeholder?: string;
}

const aspectRatioClasses = {
  square: "aspect-square",
  video: "aspect-video",
  auto: "",
};

const objectFitClasses = {
  contain: "object-contain",
  cover: "object-cover",
  fill: "object-fill",
  none: "object-none",
  "scale-down": "object-scale-down",
};

export const Image: React.FC<ImageProps> = ({
  src,
  alt,
  fallback = "/logo512.png",
  aspectRatio = "auto",
  objectFit = "cover",
  className = "",
  onError,
  useLazyLoading = false,
  rootMargin = "50px",
  placeholder,
  ...props
}) => {
  // Handle undefined or empty src: treat as "not ready to load"
  const effectiveSrc = src && src.trim() !== "" ? src : undefined;
  const [imgSrc, setImgSrc] = useState<string | undefined>(
    useLazyLoading ? undefined : effectiveSrc,
  );
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(!useLazyLoading);
  const [isLoaded, setIsLoaded] = useState(false);
  const [fallbackFailed, setFallbackFailed] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Use placeholder prop if provided, otherwise use fallback
  const placeholderSrc = placeholder || fallback;

  // Reset state when src changes
  useEffect(() => {
    const newEffectiveSrc = src && src.trim() !== "" ? src : undefined;
    if (!useLazyLoading) {
      // For non-lazy loading: update immediately if src is provided
      if (newEffectiveSrc) {
        // Only reset loaded state if the src actually changed
        const srcChanged = imgSrc !== newEffectiveSrc;
        setImgSrc(newEffectiveSrc);
        setHasError(false);
        setFallbackFailed(false);
        setIsInView(true);
        if (srcChanged) {
          setIsLoaded(false); // Reset loaded state when src changes
        }
      } else {
        // If src becomes empty/undefined, clear the image
        // Note: Parent components (like ProductCard) should track loaded images
        // and keep providing src even when not active to prevent flickering
        setImgSrc(undefined);
        setIsLoaded(false);
        setFallbackFailed(false);
      }
    } else if (isInView && newEffectiveSrc) {
      // For lazy loading that's already in view: update if src changes
      const srcChanged = imgSrc !== newEffectiveSrc;
      setImgSrc(newEffectiveSrc);
      setHasError(false);
      setFallbackFailed(false);
      if (srcChanged) {
        setIsLoaded(false); // Reset loaded state when src changes
      }
    }
  }, [src, useLazyLoading, isInView, imgSrc]);

  // Set up Intersection Observer for lazy loading
  useEffect(() => {
    if (!useLazyLoading || !imgRef.current || isInView) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            const newEffectiveSrc = src && src.trim() !== "" ? src : undefined;
            if (newEffectiveSrc) {
              setImgSrc(newEffectiveSrc);
            }
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold: 0.01,
      },
    );

    const currentRef = imgRef.current;
    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [useLazyLoading, isInView, src, rootMargin]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoaded(true);
    if (props.onLoad) {
      props.onLoad(e);
    }
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {


    if (!hasError && fallback) {
      // First error: main image failed, try fallback
      setHasError(true);
      setImgSrc(fallback);
      // Don't set isLoaded yet - wait for fallback to load
    } else {
      // Fallback failed or no fallback available
      setIsLoaded(true);
    }
    if (onError) {
      onError(e);
    }
  };

  // Determine what to show: loading placeholder, error fallback, or actual image
  // When lazy loading and not in view yet, don't set src (will be undefined/null)
  // This prevents the browser from loading the image until it's in viewport
  const displaySrc = imgSrc || (!fallbackFailed && fallback) || undefined;


  // Show placeholder if image hasn't loaded yet and we have a placeholder and a source to load
  const shouldShowPlaceholder = !isLoaded && placeholderSrc && displaySrc;

  // Check if parent wants absolute positioning (from className or style)
  const isAbsolute =
    className.includes("absolute") ||
    (props.style &&
      (props.style as React.CSSProperties).position === "absolute");

  // Only render src attribute if we have a valid source
  // This is important for lazy loading - undefined src prevents image loading
  const imgProps = displaySrc ? { src: displaySrc } : {};

  // Don't render img element at all if there's no valid source
  const shouldRenderImg = !!displaySrc || (fallbackFailed && fallback);

  // When absolute positioning, use fragment wrapper and apply absolute to both images
  if (isAbsolute) {
    return (
      <>
        {/* Placeholder - shows while image is loading */}
        {shouldShowPlaceholder && (
          <img
            src={placeholderSrc}
            alt=""
            aria-hidden="true"
            className={`
       ${className}
       ${objectFitClasses[objectFit]}
       transition-opacity duration-300
       ${isLoaded ? "opacity-0 pointer-events-none" : "opacity-100"}
      `
              .trim()
              .replace(/\s+/g, " ")}
            style={{
              ...props.style,
              filter: "blur(10px)",
              transform: "scale(1.1)",
              objectFit: objectFit as any,
              zIndex: 0,
            }}
          />
        )}
        {shouldRenderImg && (
          <img
            ref={imgRef}
            alt={alt}
            className={`
      ${aspectRatioClasses[aspectRatio]}
      ${objectFitClasses[objectFit]}
      ${className}
      ${shouldShowPlaceholder ? "transition-opacity duration-300" : ""}
      ${shouldShowPlaceholder && !isLoaded ? "opacity-0" : "opacity-100"}
     `
              .trim()
              .replace(/\s+/g, " ")}
            onLoad={handleLoad}
            onError={handleError}
            {...imgProps}
            {...props}
            style={
              shouldShowPlaceholder
                ? { ...props.style, zIndex: 1 }
                : props.style
            }
          />
        )}
      </>
    );
  }

  // Normal rendering with wrapper for placeholder
  return (
    <div
      className={`relative ${aspectRatioClasses[aspectRatio]} w-full h-full`}
      style={props.style}
    >
      {/* Placeholder - shows while image is loading */}
      {shouldShowPlaceholder && (
        <img
          src={placeholderSrc}
          alt=""
          aria-hidden="true"
          className={`
      absolute inset-0 w-full h-full
      ${objectFitClasses[objectFit]}
      ${aspectRatioClasses[aspectRatio]}
      transition-opacity duration-300
      ${isLoaded ? "opacity-0 pointer-events-none" : "opacity-100"}
     `
            .trim()
            .replace(/\s+/g, " ")}
          style={{
            filter: "blur(10px)",
            transform: "scale(1.1)",
            objectFit: objectFit as any,
            zIndex: 0,
          }}
        />
      )}
      {shouldRenderImg && (
        <img
          ref={imgRef}
          alt={alt}
          className={`
      ${aspectRatioClasses[aspectRatio]}
      ${objectFitClasses[objectFit]}
      ${className}
      ${shouldShowPlaceholder ? "transition-opacity duration-300" : ""}
      ${shouldShowPlaceholder && !isLoaded ? "opacity-0" : "opacity-100"}
     `
            .trim()
            .replace(/\s+/g, " ")}
          onLoad={handleLoad}
          onError={handleError}
          {...imgProps}
          {...props}
          style={
            shouldShowPlaceholder ? { ...props.style, zIndex: 1 } : props.style
          }
        />
      )}
    </div>
  );
};

export default Image;
